<script lang="ts">
  /**
   * SPX NET-PREMIUM PANE — R677①, the render half. Reader: `netPrem.ts` (landed `3505a61`).
   *
   * ⛔ THIS PANE DECIDES NOTHING ABOUT THE PAYLOAD. Every judgement — scope, study status,
   * staleness inputs, era, root, ordering — is made by `readNetPrem`, which is total over
   * `unknown` and returns either a view or a REFUSAL with a reason. The pane's only jobs are to
   * draw the view and to make the refusal impossible to miss.
   * ⇒ ***A PANE THAT RE-DERIVES ITS OWN VERDICT IS A SECOND CONTRACT NOBODY ARMED.***
   *
   * ⛔ ONE AXIS (law 10). The cumulative call and put tides share a single premium axis because
   * they are the same unit; nothing else is drawn on it. `TidePanel` stacks a second pane rather
   * than adding a hidden scale, and that ruling applies here — spot is NOT overlaid.
   *
   * ⛔ THE REFUSAL IS THE LOUD PATH, NOT AN EMPTY ONE (law 1). If the route ships a body this
   * reader will not accept, the operator sees the reason in words. An empty panel would read as
   * "no flow", which is a claim about the market rather than about the feed.
   *
   * ⚠ DISPLAY-ONLY UNTIL E3 RULES (law 6). The route stamps `study_status: HYPOTHESIS` and
   * `display_only: true`; the banner repeats both, and the reader refuses a bare `CALIBRATED`.
   * ⚠ ALL-EXPIRY, NEVER 0DTE (SPX-066). The 0DTE tide is a different series entirely
   * (flow-per-strike-intraday). The scope rides the banner so this pane cannot be misread.
  */
  import { onMount, onDestroy } from "svelte";
  import { fetchNetPrem, formatAge, type VenueState } from "./api";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure } from "./dataAge";
  import { readNetPrem, netPremBanner, netPremTidePaths, netPremDataAgeS, netPremClockGapLoudS, type NetPremView } from "./netPrem";
  import { reportDoorFailure, servedClosure, type DoorFaultEvidence } from "./faultEvidence";

  /**
   * `payload` is the INJECTION SEAM, and it exists for the same reason `spxNetPrem` takes a
   * `connector`: a surface that can only be driven over the network cannot be graded.
   * ⛔ Supplied ⇒ NO fetch. That is what lets the both-theme pixel gate render this pane
   * DETERMINISTICALLY, including its refusal and empty states, which live traffic never
   * reproduces on demand.
   */
  let { limit = 240, payload = undefined, venueState: injectedVenueState = null }: {
    limit?: number;
    payload?: unknown;
    venueState?: VenueState | null;
  } = $props();

  let raw = $state<unknown>(null);
  let rawFetchedAtMs = $state<number | null>(null);
  let rawRequestKey = $state<string | null>(null);
  /** Feed-level fault (network / non-200). Distinct from a payload the reader REFUSES. */
  let fault = $state<DoorFaultEvidence | null>(null);
  let retainedClockMs = $state<number | null>(null);
  let nowMs = $state(Date.now());
  let timer: ReturnType<typeof setInterval> | null = null;
  let infoOpen = $state(false);

  /**
   * RE-ENTRY GUARD. The 60s poll below is SHORTER than this request can take: the netprem
   * route runs an unbounded scan and was measured at 70-119s during the 2026-08-31 incident.
   * Without this, every tick starts another request while the previous ones are still in
   * flight — one tab holds ~2 concurrent scans permanently, and the overlap is
   * self-reinforcing because concurrency is what makes the query slower.
   * Skipping a tick is always correct here: the in-flight request writes the same `raw`.
   */
  let inFlight = false;

  async function load() {
    if (inFlight) return;
    inFlight = true;
    const requestKey = `/api/spx/net-prem?limit=${limit}`;
    try {
      const next = await fetchNetPrem(limit);
      raw = next;
      rawFetchedAtMs = Date.now();
      rawRequestKey = requestKey;
      const accepted = readNetPrem(next);
      if (accepted.ok) {
        const vendorClock = accepted.vendorTimeNewest === null ? Number.NaN : Date.parse(accepted.vendorTimeNewest);
        retainedClockMs = Number.isFinite(vendorClock) ? vendorClock : Date.now();
      }
      fault = null;
    } catch (err) {
      // Keep the last good payload and STATE the fault — never silently empty.
      nowMs = Date.now();
      fault = reportDoorFailure("net-prem", err, retainedClockMs, nowMs);
      raw = keptPayloadAfterFailure(raw, rawRequestKey, requestKey);
      if (raw === null) {
        rawFetchedAtMs = null;
        rawRequestKey = null;
      }
    } finally {
      inFlight = false;
    }
  }

  onMount(() => {
    // An injected payload is a fixture, not a feed: never poll it, never overwrite it.
    if (payload !== undefined) {
      raw = payload;
      return;
    }
    load();
    timer = setInterval(load, 60_000);
    const clock = setInterval(() => (nowMs = Date.now()), 1_000);
    return () => clearInterval(clock);
  });
  onDestroy(() => {
    if (timer !== null) clearInterval(timer);
  });

  let verdict = $derived(raw === null ? null : readNetPrem(raw));
  let view = $derived<NetPremView | null>(verdict?.ok ? verdict : null);
  let refusal = $derived(verdict && !verdict.ok ? verdict.reason : null);

  /** Cumulative call/put tides on ONE shared premium axis — math armed in `netPrem.ts`. */
  const W = 320;
  const H = 84;
  let path = $derived(netPremTidePaths(view?.buckets ?? [], W, H));

  /**
   * ⛔ TWO CLOCKS, EACH NAMING ITS SERIES (TidePanel §3). MEASURED ON THE LIVE ROUTE 23:5xZ:
   * `age_s` said 11.5h while the newest tape was ** 75.0h ** old — the capture clock, not the
   * data clock. One chip carrying only the first reads FRESH over three-day-old tape.
   */
  let dataAgeS = $derived(netPremDataAgeS(view?.buckets ?? [], Date.now()));
  /** The gap between what we LAST LOOKED and what we LAST GOT. Loud when it is material. */
  let clockGapS = $derived(
    view !== null && view.ageS !== null && dataAgeS !== null ? dataAgeS - view.ageS : null,
  );
  /** The face's gap span: loud only past the budget (ORDER_HUD_APP_BUILD_ALL_GREEN_1 D1 (a)). */
  let loudGapS = $derived(view === null ? null : netPremClockGapLoudS(view, dataAgeS));

  let venueState = $derived(injectedVenueState);
  let venueClosed = $derived(servedClosure(venueState));
  let freshnessStale = $derived(!venueClosed && (fault !== null || view?.stale === true));
  let freshnessTitle = $derived(
    view === null ? "net-prem freshness unserved" : `net-prem capture age ${view.ageS ?? "unreported"}s`,
  );
</script>

<section class="card" aria-label="SPX net premium">
  <header class="panel-id">
    <h2>SPX NET PREMIUM</h2>
    <PanelFreshness label="net-prem" venueState={venueClosed} stale={freshnessStale} ageTitle={freshnessTitle} />
    <AgePill payload={raw} fetchedAtMs={rawFetchedAtMs} door={rawRequestKey ?? `/api/spx/net-prem?limit=${limit}`} />
    <!-- THE SECOND CLOCK on the face (the data's own age beside the capture pill); de569c50 had moved it behind
         the info panel, where a 63h divergence drew silently. The prose stays in the info panel (INK-3). -->
    {#if view}<span class="sub" data-np="dataage">tape {dataAgeS === null ? "unreported" : formatAge(dataAgeS)}</span>{/if}
    {#if loudGapS !== null}<span class="gap" data-np="clockgap">⛔ capture clock {formatAge(loudGapS)} younger</span>{/if}
    <span class="chip hyp" data-hyp>HYP</span>
    <PanelInfo id="net-prem-info" label="Net premium information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      <p>{view ? netPremBanner(view) : "ALL-EXPIRY · HYPOTHESIS · DISPLAY-ONLY"}</p>
      <p>newest tape {dataAgeS === null ? "unreported" : `${formatAge(dataAgeS)} old`}</p>
      {#if clockGapS !== null && view}<p>capture clock is {formatAge(clockGapS)} younger than the data</p>{/if}
      <p>cum net CALL premium · cum net PUT premium · derived by spx-hub · gaps never interpolated</p>
      {#if view?.vendorTimeNewest === null}<p>vendor clock not served</p>{/if}
      {#if view}<p>{view.counts.live} live · {view.counts.backfill} backfill · {view.counts.buckets} buckets</p>{/if}
    </PanelInfo>
  </header>

  {#if fault}
    <p class="loud"><b>DOOR FAILURE</b> · {fault.label}{#if fault.retained} · {fault.retained}{/if}</p>
  {/if}

  {#if refusal}
    <!-- The route answered and this reader will not draw it. Say which, and why. -->
    <p class="loud">PAYLOAD REFUSED — {refusal}</p>
  {:else if view === null}
    <p class="sub" aria-label="loading net premium">●</p>
  {:else if view.empty}
    <!-- `empty_reason` distinguishes "nothing captured" from "captured and REFUSED as
         malformed". Collapsing them would blame the market for a capture fault. -->
    <p class="loud">NO DATA — {view.emptyReason ?? "the route reported empty without a reason"}</p>
  {:else if path.call === ""}
    <p class="state" role="status">NO DRAWABLE TIDE</p>
  {:else}
    <svg class="tide" viewBox="0 0 {W} {H}" preserveAspectRatio="none" role="img"
         aria-label="cumulative net call and put premium">
      <path class="call" d={path.call} />
      <path class="put" d={path.put} />
    </svg>
  {/if}
</section>

<style>
  /* Tokens only — law 8. */
  .card {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow);
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 8px;
    gap: 6px;
  }
  .hd { display: flex; align-items: center; gap: 8px; }
  h2 { font-size: 11px; letter-spacing: 0.06em; margin: 0; color: var(--ink); }
  .sub { font-size: 9px; color: var(--muted); margin: 0; }
  .hypo { color: var(--stale-ink); font-weight: 700; }
  .chip.hyp { border: 1px solid var(--line); border-radius: var(--radius-pill); background: var(--chip); color: var(--ink); padding: 1px 6px; font: 700 10px var(--font-num); }
  .offnote { color: var(--faint); }
  .gap { color: var(--stale-ink); font-weight: 700; }
  .tide { width: 100%; height: 84px; display: block; }
  .tide path { fill: none; stroke-width: 1.5; vector-effect: non-scaling-stroke; }
  .tide path.call { stroke: var(--cvd-pos); }
  .tide path.put { stroke: var(--cvd-neg); }
  .lgd { display: flex; flex-wrap: wrap; gap: 8px; font-size: 9px; color: var(--muted); }
  .sw { display: inline-block; width: 8px; height: 8px; margin-right: 3px; vertical-align: -1px; }
  .sw.call { background: var(--cvd-pos); }
  .sw.put { background: var(--cvd-neg); }
  /*
    The fault box borrows the panel's established amber pair. --stale-ink, not --stale, on
    --stale-soft: the palette receipt measures --stale on --stale-soft at 3.14:1, which fails
    text contrast — the ink token exists precisely because the border colour cannot carry copy.
  */
  .loud {
    background: var(--stale-soft);
    border: 1px solid var(--stale);
    border-radius: 4px;
    color: var(--stale-ink);
    font-size: 10px;
    margin: 0;
    padding: 6px;
  }
</style>
